import React, { useState } from 'react'
import { colors } from './fieldTheme'
import { countObjects } from './fieldModel'
import { themeState, progress, confirmAllWording, referenceLabel, referenceWhen, plural } from './fieldReference'
import { guestName } from './fieldStays'
import { pitchSubtitle } from './fieldSearch'
import { CheckIcon, CircleIcon, WarningIcon, ClearIcon, ThemeIllustration, InfoIcon, WrenchIcon, HistoryIcon } from './FieldIcons'
import { FLOW_TYPES, Pastille, BottomSheet, SheetGroup, SheetRow, LinkButton, PrimaryButton, SoftSearchField, RowLink, ErrorNote } from './FieldUI'
import FieldInterventionSheet from './FieldInterventionSheet'
import FieldSearchAll from './FieldSearchAll'

// Vue des thèmes (canvas W7), confirmation globale (W7b) et recherche dans tous les thèmes (W7e).
// Décisions 24 et 36. « Signaler un problème » (W7c, décision 43) : action secondaire sous la grille,
// qui ouvre le formulaire allégé d'intervention ; on revient ensuite aux thèmes.

const STATE_STYLES = {
	ok: { background: '#DCFCE7', color: '#14532D' },
	todo: { background: '#E8EBF5', color: colors.primary },
	gaps: { background: '#FEE2E2', color: '#7F1D1D' }
}

export function StatusPill({ state, gaps })
{
	const style = STATE_STYLES[state]
	let icon = <CircleIcon />
	let label = 'À faire'
	if (state === 'ok')
	{
		icon = <CheckIcon size={20} />
		label = 'Conforme'
	}
	else if (state === 'gaps')
	{
		icon = <WarningIcon size={20} />
		label = plural(gaps, 'écart')
	}
	return (
		<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 32, padding: '0 12px 0 8px', borderRadius: 999, background: style.background, color: style.color, fontSize: 15, fontWeight: 700, flex: 'none' }}>
			{icon}
			{label}
		</span>
	)
}

function ThemeCard({ theme, status, wide, onClick })
{
	const illustration = (
		<span style={{ width: 60, height: 60, borderRadius: 999, background: '#E8EBF5', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
			<ThemeIllustration name={theme.name} />
		</span>
	)
	const base = { backgroundImage: 'none', backgroundColor: colors.surface, border: '2px solid ' + colors.border, borderRadius: 22, padding: 20, color: colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }
	const count = plural(theme.objects.length, 'objet')
	if (wide)
	{
		return (
			<button type="button" className="field-press" onClick={onClick} style={{ ...base, gridColumn: 'span 2', minHeight: 96, display: 'flex', alignItems: 'center', gap: 14 }}>
				{illustration}
				<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
					<span lang="fr" style={{ fontSize: 17, fontWeight: 700, overflowWrap: 'break-word', hyphens: 'auto' }}>{theme.name}</span>
					<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{count}</span>
				</span>
				<StatusPill state={status.state} gaps={status.gaps} />
			</button>
		)
	}
	return (
		<button type="button" className="field-press" onClick={onClick} style={{ ...base, minWidth: 0, minHeight: 176, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
			{illustration}
			<span lang="fr" style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25, overflowWrap: 'break-word', hyphens: 'auto', maxWidth: '100%' }}>{theme.name}</span>
			<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, marginTop: -6 }}>{count}</span>
			<span style={{ marginTop: 'auto' }}><StatusPill state={status.state} gaps={status.gaps} /></span>
		</button>
	)
}

// En-tête d'identité : pastille, « M066 · Sortie », vacancier, séjour, référence (une ligne chacun)
export function InventoryIdentity({ type, pitch, stay, client, lines })
{
	const name = guestName(client)
	const details = stay
		? [name, 'Séjour n° ' + stay.stay_number].filter(Boolean)
		: [pitchSubtitle(pitch)].filter(Boolean)
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '24px 20px 0' }}>
			<Pastille type={type} size={52} illustrationSize={38} />
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25, gap: 2 }}>
				<h1 style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2, color: colors.text, overflowWrap: 'anywhere' }}>{pitch.location + ' · ' + FLOW_TYPES[type].short}</h1>
				{details.concat(lines || []).map(line => (
					<span key={line} style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, whiteSpace: 'pre-line' }}>{line}</span>
				))}
			</span>
		</div>
	)
}

export function CloseButton({ onClick })
{
	return (
		<button
			type="button"
			className="field-solid"
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 48, padding: '0 18px 0 12px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
		>
			<ClearIcon />
			Fermer
		</button>
	)
}

// Quitter un inventaire commencé : rien n'est encore enregistré. Pour un inventaire du tour, « Enregistrer pour plus tard »
// (onSaveLater) le met de côté sur le serveur, sans le déposer, pour le reprendre là où la saisie s'arrête ; restored :
// la saisie était déjà reprise d'un enregistrement précédent.
export function LeaveSheet({ onLeave, onStay, onSaveLater, saving, saveError, restored })
{
	let text = 'Vos comptages seront perdus : rien n\'a encore été enregistré.'
	if (onSaveLater)
		text = restored ? 'Enregistrez pour plus tard, sinon ce que vous avez fait depuis la reprise sera perdu.' : 'Enregistrez pour plus tard pour le reprendre là où vous en êtes, sinon vos comptages seront perdus.'
	return (
		<BottomSheet title="Quitter l'inventaire ?" text={text} onClose={saving ? () => null : onStay}>
			{saveError && <ErrorNote>L'inventaire n'a pas pu être mis de côté. Il faut du réseau : vérifiez la connexion, puis réessayez.</ErrorNote>}
			<SheetGroup>
				<SheetRow label="Continuer l'inventaire" icon={<CheckIcon />} onClick={saving ? () => null : onStay} />
				{onSaveLater && <SheetRow label={saving ? 'Enregistrement…' : (saveError ? 'Réessayer d\'enregistrer pour plus tard' : 'Enregistrer pour plus tard')} icon={<HistoryIcon />} onClick={saving ? () => null : onSaveLater} />}
			</SheetGroup>
			<SheetGroup>
				<SheetRow label="Quitter sans enregistrer" icon={<ClearIcon />} danger onClick={saving ? () => null : onLeave} />
			</SheetGroup>
		</BottomSheet>
	)
}

export default function FieldThemes({ notice, type, pitch, stay, client, themes, reference, counts, doneThemes, dirty, now, user, problems, inventoryPhotos, inventoryComments, loadUsers, onReportProblem, onOpenTheme, onCount, onConfirmAll, onReview, onClose, onSaveLater, savingLater, saveLaterError, restored })
{
	const [confirmOpen, setConfirmOpen] = useState(false)
	const [leaveOpen, setLeaveOpen] = useState(false)
	const [problemOpen, setProblemOpen] = useState(false)
	const [query, setQuery] = useState('')
	const searching = query.trim() !== ''
	const state = progress(themes, doneThemes)
	const wording = confirmAllWording(themes, counts, reference, doneThemes)
	const odd = themes.length % 2 === 1
	const objectsLabel = wording ? (wording.others ? 'les ' + wording.count + ' autres objets' : 'les ' + wording.count + ' objets') : ''
	const confirmText = 'Vous confirmez que ' + objectsLabel + (reference.source === 'inventory'
		? ' sont comme au dernier inventaire (' + referenceWhen(reference, now) + ') ?'
		: ' sont conformes au modèle de l\'hébergement ?')

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 56, padding: '4px 20px 0' }}>
				<CloseButton onClick={() => (dirty ? setLeaveOpen(true) : onClose())} />
			</div>
			<InventoryIdentity type={type} pitch={pitch} stay={stay} client={client} lines={[referenceLabel(reference, now)]} />
			{notice && (
				<div role="note" style={{ margin: '16px 20px 0', display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, borderRadius: 18, background: '#E8EBF5', color: colors.text }}>
					<InfoIcon size={24} />
					<span style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.35 }}>
						<span style={{ fontSize: 17, fontWeight: 700 }}>{notice.title}</span>
						<span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{notice.text}</span>
					</span>
				</div>
			)}

			<div style={{ padding: '32px 20px 0' }}>
				<SoftSearchField
					id="field-search-all"
					label="Chercher un objet dans tous les thèmes"
					placeholder="Chercher un objet"
					value={query}
					onChange={setQuery}
					onClear={() => setQuery('')}
				/>
			</div>

			{searching && (
				<FieldSearchAll query={query} themes={themes} reference={reference} counts={counts} onCount={onCount} onDone={() => setQuery('')} />
			)}

			{!searching && (
				<React.Fragment>
					<div style={{ padding: '32px 20px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
						<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: colors.textSecondary }}>
							<span>{state.done + (state.done > 1 ? ' thèmes sur ' : ' thème sur ') + state.total + (state.done > 1 ? ' faits' : ' fait')}</span>
							<span>{plural(countObjects(themes), 'objet')}</span>
						</div>
						<div aria-hidden="true" style={{ height: 10, borderRadius: 5, background: colors.separator, overflow: 'hidden' }}>
							<span style={{ display: 'block', height: '100%', width: (state.total ? Math.round(100 * state.done / state.total) : 0) + '%', borderRadius: 5, background: colors.primary, transition: 'width 300ms ease-out' }} />
						</div>
					</div>

					<div style={{ margin: '16px 20px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
						{themes.map((theme, index) => (
							<ThemeCard
								key={theme.id}
								theme={theme}
								status={themeState(theme, counts, reference, doneThemes)}
								wide={themes.length === 1 || (odd && index === themes.length - 1)}
								onClick={() => onOpenTheme(theme.id)}
							/>
						))}
					</div>

					{wording && (
						<div style={{ display: 'flex', justifyContent: 'center', padding: '24px 20px 0' }}>
							<LinkButton icon={<CheckIcon />} onClick={() => setConfirmOpen(true)}>{wording.link}</LinkButton>
						</div>
					)}

					<div style={{ padding: '16px 20px 0' }}>
						<RowLink icon={<WrenchIcon size={26} color="currentColor" />} title="Signaler un problème" subtitle={problems > 0 ? plural(problems, 'problème signalé', 'problèmes signalés') : 'Fuite, casse, saleté : une intervention tout de suite'} onClick={() => setProblemOpen(true)} />
					</div>

					<div style={{ position: 'sticky', bottom: 0, marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8, padding: '24px 20px', paddingBottom: 'max(28px, env(safe-area-inset-bottom))', background: colors.background }}>
						{state.left > 0 && (
							<p id="field-review-reason" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>
								<InfoIcon />
								{plural(state.left, 'thème') + ' à vérifier'}
							</p>
						)}
						<PrimaryButton disabled={state.left > 0} onClick={onReview}>Voir le récapitulatif</PrimaryButton>
					</div>
				</React.Fragment>
			)}

			{confirmOpen && wording && (
				<BottomSheet title={wording.link + ' ?'} text={confirmText} onClose={() => setConfirmOpen(false)}>
					<SheetGroup>
						<SheetRow label="Oui, j'ai vérifié" icon={<CheckIcon />} onClick={() => {
							setConfirmOpen(false)
							onConfirmAll()
						}} />
					</SheetGroup>
					<SheetGroup>
						<SheetRow label="Annuler" icon={<ClearIcon />} onClick={() => setConfirmOpen(false)} />
					</SheetGroup>
				</BottomSheet>
			)}
			{leaveOpen && <LeaveSheet onLeave={onClose} onStay={() => setLeaveOpen(false)} onSaveLater={onSaveLater} saving={savingLater} saveError={saveLaterError} restored={restored} />}
			{problemOpen && (
				<FieldInterventionSheet
					start="problem"
					singleScroll
					missing={[]}
					inventoryPhotos={inventoryPhotos}
					inventoryComments={inventoryComments}
					pitch={pitch}
					stay={stay}
					user={user}
					loadUsers={loadUsers}
					onCreate={(payload) => onReportProblem(payload).then(() => setProblemOpen(false))}
					onClose={() => setProblemOpen(false)}
				/>
			)}
		</div>
	)
}
